import React, { useState, useEffect, useRef } from "react";
import "./MessageDialog.css";
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from "react-redux";
import { setMessageText, resetMessageDialog, SEND_DIRECT_MESSAGE, SEND_ROOM_MESSAGE } from "../../../../store/messagesSlice/messagesSlice.js";
import { setOpenModal } from "../../store/controlPanelSlice";
import { uploadMessageFile } from "../../../../store/messagesSlice/messagesThunks.js";
import AddIconSvg from "../../../../assets/add.svg";
import Popover from "@mui/material/Popover";
import EmojiPicker from "emoji-picker-react";
import MoodIconSvg from "../../../../assets/mood.svg";
import SendIconSvg from "../../../../assets/send.svg";
import {
  IconButton,
} from '@mui/material';
const formatBytes = (bytes) => {
  if (bytes === undefined || bytes === null) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n = n / 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};


const MessageDialog = ({ className }) => {
    const { messageText, isRoom, target } = useSelector(state => state.messageHistory);
    const { localAvatar } = useSelector(state => state.auth);
    const dispatch = useDispatch();
    const { t } = useTranslation()
    const { mapConfig } = useSelector((state) => state.MapDisplay);
    const email = localAvatar.email;

  const [pendingFile, setPendingFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const [sendError, setSendError] = useState(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const emojiOpen = Boolean(emojiAnchor);
  const handleClose = () => {
    setPendingFile(null);
    setSendError(null);
    setEmojiAnchor(null);
    dispatch(resetMessageDialog());
    dispatch(setOpenModal(null));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPendingFile(file);

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

    const handleSend = async () => {
    const hasText = !!(messageText || "").trim();
    const hasFile = !!pendingFile;

    if (!hasText && !hasFile) return;

    try {
      setIsUploading(true);

      if (hasFile) {
        const formData = new FormData();
        formData.append("file", pendingFile);

        const result = await dispatch(uploadMessageFile({ body: formData })).unwrap();

        const basePayload = {
          sender: email,
          message: (messageText || "").trim() || "",
          fileName: result.fileName,
          fileType: result.fileType,
          fileUrl: result.url,
          fileSize: result.fileSize,
          map_id: mapConfig.mapId,
          tenantId: localAvatar.tenant_id,
        };

        if (isRoom) {
          dispatch(
            SEND_ROOM_MESSAGE({
              ...basePayload,
              roomId: target?.id ? target.id : target?.room_id,
            })
          );
        } else {
          dispatch(
            SEND_DIRECT_MESSAGE({
              ...basePayload,
              recipientEmail: target.email,
            })
          );
        }

        handleClose();
        return;
      }

      if (isRoom) {
        dispatch(
          SEND_ROOM_MESSAGE({
            sender: email,
            roomId: target?.id ? target.id : target?.room_id,
            message: messageText,
            map_id: mapConfig.mapId,
          })
        );
      } else {
        dispatch(
          SEND_DIRECT_MESSAGE({
            message: messageText,
            recipientEmail: target.email,
            sender: email,
            map_id: mapConfig.mapId,
          })
        );
      }

      handleClose();
    } catch (err) {
      setSendError(t("FILE_TYPE_NOT_ALLOWED"));
    } finally {
      setIsUploading(false);
    }
  };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }

    };


    const directName = target?.display_name || target?.name || "";
  const title = isRoom
    ? t("message_dialog_title_room")
    : t("message_dialog_title_direct", { name: directName });

  const placeholder = isRoom
    ? t("message_dialog_placeholder_room")
    : t("message_dialog_placeholder_direct", { name: directName });

    return (
        <>

        <div className="dialog-overlay dialog-overlay-message" onMouseDown={handleClose}>
            <div className={className} onMouseDown={(e) => e.stopPropagation()}>
            <div className="dialog-header">
                <div className="dialog-header-left">
                <span className="material-symbols-outlined dialog-header-icon">mail</span>
                <div className="dialog-title">{title}</div>
                </div>

                <button className="dialog-close" onClick={handleClose} aria-label={t("close")}>
                    &times;
                </button>
          </div>

                {pendingFile && (
                <div className="dialog-file-preview">
                <span className="dialog-file-icon material-symbols-outlined">attach_file</span>
                <div className="dialog-file-meta">
                    <div className="dialog-file-name">{pendingFile.name}</div>
                    <div className="dialog-file-sub">{formatBytes(pendingFile.size)}</div>
                </div>
                <button
                    className="dialog-file-remove"
                    type="button"
                    onClick={() => setPendingFile(null)}
                    disabled={isUploading}
                    title={t("remove")}
                >
                    <span className="material-symbols-outlined">close</span>
                </button>
                </div>
            )}

          <div className="dialog-composer">
            <label
              className={`dialog-attach-btn ${isUploading ? "disabled" : ""}`}
              title={t("add_file")}
            >
                    <img src={AddIconSvg} alt="add" />
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        onChange={handleFileUpload}
                        disabled={isUploading}
                    />
                    </label>
                    <div className="dialog-input-wrapper">
                    <textarea
              ref={textareaRef}
              className="dialog-textarea dialog-textarea--composer"
              placeholder={placeholder}
              value={messageText}
              onChange={(e) => dispatch(setMessageText(e.target.value))}
              onKeyDown={handleKeyDown}
            />
            <img
                src={MoodIconSvg}
                alt="emoji"
                className="dialog-emoji-icon"
                onClick={(e) => setEmojiAnchor(emojiOpen ? null : e.currentTarget)}
                title={t("add_emoji")}
            />
            </div>
            <IconButton
                        className="figma-send-btn"
                        onClick={handleSend}
                        disabled={isUploading || (!messageText.trim() && !pendingFile)}
                      >
                        <img src={SendIconSvg} alt="send" className="figma-send-icon" />
            </IconButton>
          </div>
          <Popover
            open={emojiOpen}
            anchorEl={emojiAnchor}
            onClose={() => setEmojiAnchor(null)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            transformOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <EmojiPicker
              onEmojiClick={(emojiData) => {
                dispatch(setMessageText((messageText || "") + emojiData.emoji));
                setEmojiAnchor(null);
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
            />
          </Popover>
            </div>
        </div>
        </>
    );
};

export default MessageDialog;
